/**
 * Timeline Aggregation Service
 * 
 * Background service that automatically populates the interaction_timeline table
 * Runs periodically to sync all emails, meetings, and journals into a unified timeline
 */

import { blink } from '@/lib/blink';

export class TimelineAggregationService {
  private isRunning = false;
  private intervalId: NodeJS.Timeout | null = null;
  private lastSyncTime = 0;
  
  // Rate limiting configuration - more conservative
  private readonly BATCH_SIZE = 25; // Smaller batches to avoid rate limits
  private readonly BATCH_DELAY_MS = 1000; // Longer delay between batches (1 second)
  private readonly MAX_RETRIES = 5; // More retries for rate limit errors
  private readonly INITIAL_RETRY_DELAY_MS = 2000; // Start with 2 second delay
  private readonly MIN_SYNC_INTERVAL_MS = 30000; // Minimum 30 seconds between syncs
  private readonly REQUEST_DELAY_MS = 200; // Delay between individual requests

  /**
   * Start the background sync process
   */
  start(intervalMs: number = 60000) { // Run every minute by default
    if (this.intervalId) {
      console.log('⚠️ Timeline aggregation already running');
      return;
    }

    console.log('✅ Starting timeline aggregation service');
    
    // Don't run immediately - wait for the interval to avoid initial load spike
    // This prevents rate limiting on page load
    
    // Then run periodically
    this.intervalId = setInterval(() => {
      this.syncTimeline();
    }, intervalMs);
  }

  /**
   * Stop the background sync process
   */
  stop() {
    if (this.intervalId) {
      clearInterval(this.intervalId);
      this.intervalId = null;
      console.log('🛑 Timeline aggregation service stopped');
    }
  }

  /**
   * Sync all interactions into the timeline
   */
  private async syncTimeline() {
    if (this.isRunning) {
      console.log('⏭️ Timeline sync already in progress, skipping...');
      return;
    }

    // Check if enough time has passed since last sync
    const now = Date.now();
    const timeSinceLastSync = now - this.lastSyncTime;
    if (timeSinceLastSync < this.MIN_SYNC_INTERVAL_MS && this.lastSyncTime > 0) {
      console.log(`⏳ Skipping sync - only ${Math.round(timeSinceLastSync / 1000)}s since last sync (minimum ${this.MIN_SYNC_INTERVAL_MS / 1000}s)`);
      return;
    }

    this.isRunning = true;
    const startTime = Date.now();

    try {
      // Check if user is authenticated
      const user = await blink.auth.me();
      if (!user?.id) {
        console.log('⚠️ No authenticated user, skipping timeline sync');
        return;
      }

      this.lastSyncTime = now;
      let totalSynced = 0;

      // 1. Sync emails from messages_enhanced
      console.log('📧 Starting email sync...');
      const emailCount = await this.syncEmails(user.id);
      totalSynced += emailCount;
      console.log(`✅ Email sync complete: ${emailCount} items`);

      // 2. Sync meetings from calendar events
      console.log('📅 Starting meeting sync...');
      const meetingCount = await this.syncMeetings(user.id);
      totalSynced += meetingCount;
      console.log(`✅ Meeting sync complete: ${meetingCount} items`);

      // 3. Sync voice journals
      console.log('🎙️ Starting journal sync...');
      const journalCount = await this.syncVoiceJournals(user.id);
      totalSynced += journalCount;
      console.log(`✅ Journal sync complete: ${journalCount} items`);

      const duration = Date.now() - startTime;
      console.log(`✅ Timeline sync complete: ${totalSynced} items synced in ${duration}ms`);
    } catch (error: any) {
      // Gracefully handle authentication errors - don't log as errors
      if (error?.code === 'INVALID_CREDENTIALS' || error?.name === 'BlinkAuthError') {
        console.log('⚠️ Timeline sync skipped - user not authenticated');
      } else {
        console.error('❌ Timeline sync error:', error);
      }
    } finally {
      this.isRunning = false;
    }
  }

  /**
   * Retry a database operation with exponential backoff
   */
  private async retryWithBackoff<T>(
    operation: () => Promise<T>,
    retries = this.MAX_RETRIES,
    delay = this.INITIAL_RETRY_DELAY_MS,
    operationName = 'Database operation'
  ): Promise<T> {
    try {
      // Add a small delay before every request to avoid rate limiting
      await this.delay(this.REQUEST_DELAY_MS);
      return await operation();
    } catch (error: any) {
      // Check if it's a rate limit error
      if (error?.message?.includes('Rate limit exceeded') && retries > 0) {
        const waitTime = error.message.match(/(\d+) seconds/)?.[1];
        const delayMs = waitTime ? (parseInt(waitTime) * 1000) + 1000 : delay; // Add 1 second buffer
        
        console.log(`⏳ ${operationName}: Rate limited, waiting ${Math.round(delayMs / 1000)}s before retry (${retries} retries left)...`);
        await this.delay(delayMs);
        
        return this.retryWithBackoff(operation, retries - 1, delay * 2, operationName);
      }
      
      // Check if it's a 429 status error
      if (error?.status === 429 && retries > 0) {
        const delayMs = delay;
        console.log(`⏳ ${operationName}: HTTP 429, waiting ${Math.round(delayMs / 1000)}s before retry (${retries} retries left)...`);
        await this.delay(delayMs);
        
        return this.retryWithBackoff(operation, retries - 1, delay * 2, operationName);
      }
      
      throw error;
    }
  }

  /**
   * Delay helper
   */
  private delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  /**
   * Sync emails to interaction_timeline
   */
  private async syncEmails(userId: string): Promise<number> {
    let count = 0;

    try {
      // Get all messages with retry - limit to prevent overwhelming the database
      const messages = await this.retryWithBackoff(
        () =>
          blink.db.messagesEnhanced.list({
            where: { userId },
            orderBy: { sentDate: 'desc' },
            limit: 100, // Reduced limit to avoid rate limiting
          }),
        this.MAX_RETRIES,
        this.INITIAL_RETRY_DELAY_MS,
        'Fetch emails'
      );

      // Process in batches to avoid rate limiting
      for (let i = 0; i < messages.length; i += this.BATCH_SIZE) {
        const batch = messages.slice(i, i + this.BATCH_SIZE);
        
        for (const message of batch) {
          try {
            // Extract person_id from sender or recipients
            const senderEmail = message.senderEmail?.toLowerCase() || '';
            
            // Try to find person_id from contact intelligence
            let personId = '';
            if (senderEmail) {
              const contacts = await this.retryWithBackoff(
                () =>
                  blink.db.contactIntelligenceUnified.list({
                    where: { userId, contactEmail: senderEmail },
                    limit: 1,
                  }),
                this.MAX_RETRIES,
                this.INITIAL_RETRY_DELAY_MS,
                `Find contact ${senderEmail}`
              );
              personId = contacts[0]?.personId || '';
            }

            // If no person_id, create one based on email
            if (!personId) {
              personId = `person_${senderEmail.replace(/[^a-z0-9]/g, '_')}`;
            }

            // Check if already in timeline
            const existing = await this.retryWithBackoff(
              () =>
                blink.db.interactionTimeline.list({
                  where: { userId, emailId: message.id },
                  limit: 1,
                }),
              this.MAX_RETRIES,
              this.INITIAL_RETRY_DELAY_MS,
              'Check timeline'
            );

            if (existing.length === 0) {
              const interactionDate = message.sentDate || message.receivedDate || message.createdAt;
              
              await this.retryWithBackoff(
                () =>
                  blink.db.interactionTimeline.create({
                    id: `timeline_email_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`,
                    userId,
                    personId,
                    interactionType: 'email',
                    interactionDate,
                    emailId: message.id,
                    channel: 'email',
                    subject: message.subject || 'No subject',
                    snippet: message.snippet || message.bodyText?.substring(0, 200) || '',
                    content: message.bodyText || '',
                    participants: message.toRecipients || '[]',
                    attachments: Number(message.hasAttachments) > 0 ? 'true' : 'false',
                    createdAt: new Date().toISOString(),
                    updatedAt: new Date().toISOString(),
                  }),
                this.MAX_RETRIES,
                this.INITIAL_RETRY_DELAY_MS,
                'Create timeline entry'
              );
              count++;
            }
          } catch (error) {
            // Silently skip individual errors
          }
        }
        
        // Delay between batches to avoid rate limiting
        if (i + this.BATCH_SIZE < messages.length) {
          await this.delay(this.BATCH_DELAY_MS);
        }
      }
    } catch (error) {
      console.error('Error syncing emails:', error);
    }

    return count;
  }

  /**
   * Sync meetings to interaction_timeline
   */
  private async syncMeetings(userId: string): Promise<number> {
    let count = 0;

    try {
      // Get all calendar events with retry - limit to prevent overwhelming the database
      const events = await this.retryWithBackoff(
        () =>
          blink.db.calendarEventsEnhanced.list({
            where: { userId },
            orderBy: { startTime: 'desc' },
            limit: 100, // Reduced limit to avoid rate limiting
          }),
        this.MAX_RETRIES,
        this.INITIAL_RETRY_DELAY_MS,
        'Fetch meetings'
      );

      // Process in batches to avoid rate limiting
      for (let i = 0; i < events.length; i += this.BATCH_SIZE) {
        const batch = events.slice(i, i + this.BATCH_SIZE);
        
        for (const event of batch) {
          try {
            // Extract person_id from organizer or attendees
            let personId = '';
            const organizerEmail = event.organizerEmail?.toLowerCase() || '';
            
            if (organizerEmail) {
              const contacts = await this.retryWithBackoff(
                () =>
                  blink.db.contactIntelligenceUnified.list({
                    where: { userId, contactEmail: organizerEmail },
                    limit: 1,
                  }),
                this.MAX_RETRIES,
                this.INITIAL_RETRY_DELAY_MS,
                `Find organizer ${organizerEmail}`
              );
              personId = contacts[0]?.personId || `person_${organizerEmail.replace(/[^a-z0-9]/g, '_')}`;
            } else {
              personId = 'person_unknown_meeting';
            }

            // Check if already in timeline
            const existing = await this.retryWithBackoff(
              () =>
                blink.db.interactionTimeline.list({
                  where: { userId, meetingId: event.id },
                  limit: 1,
                }),
              this.MAX_RETRIES,
              this.INITIAL_RETRY_DELAY_MS,
              'Check meeting timeline'
            );

            if (existing.length === 0) {
              await this.retryWithBackoff(
                () =>
                  blink.db.interactionTimeline.create({
                    id: `timeline_meeting_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`,
                    userId,
                    personId,
                    interactionType: 'meeting',
                    interactionDate: event.startTime,
                    meetingId: event.id,
                    channel: 'meeting',
                    subject: event.title || 'Meeting',
                    snippet: event.aiSummary || event.description || '',
                    content: event.description || '',
                    participants: event.attendees || '[]',
                    outcomes: event.aiAgenda || '',
                    createdAt: new Date().toISOString(),
                    updatedAt: new Date().toISOString(),
                  }),
                this.MAX_RETRIES,
                this.INITIAL_RETRY_DELAY_MS,
                'Create meeting timeline entry'
              );
              count++;
            }
          } catch (error) {
            // Silently skip individual errors
          }
        }
        
        // Delay between batches to avoid rate limiting
        if (i + this.BATCH_SIZE < events.length) {
          await this.delay(this.BATCH_DELAY_MS);
        }
      }
    } catch (error) {
      console.error('Error syncing meetings:', error);
    }

    return count;
  }

  /**
   * Sync voice journals to interaction_timeline
   */
  private async syncVoiceJournals(userId: string): Promise<number> {
    let count = 0;

    try {
      // Get all voice journals with retry - limit to prevent overwhelming the database
      const journals = await this.retryWithBackoff(
        () =>
          blink.db.voiceJournals.list({
            where: { userId },
            orderBy: { recordedAt: 'desc' },
            limit: 100, // Reduced limit to avoid rate limiting
          }),
        this.MAX_RETRIES,
        this.INITIAL_RETRY_DELAY_MS,
        'Fetch voice journals'
      );

      // Process in batches to avoid rate limiting
      for (let i = 0; i < journals.length; i += this.BATCH_SIZE) {
        const batch = journals.slice(i, i + this.BATCH_SIZE);
        
        for (const journal of batch) {
          try {
            const personId = journal.personId || 'person_unknown_journal';

            // Check if already in timeline
            const existing = await this.retryWithBackoff(
              () =>
                blink.db.interactionTimeline.list({
                  where: { userId, voiceJournalId: journal.id },
                  limit: 1,
                }),
              this.MAX_RETRIES,
              this.INITIAL_RETRY_DELAY_MS,
              'Check journal timeline'
            );

            if (existing.length === 0) {
              await this.retryWithBackoff(
                () =>
                  blink.db.interactionTimeline.create({
                    id: `timeline_journal_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`,
                    userId,
                    personId,
                    interactionType: 'voice_journal',
                    interactionDate: journal.recordedAt,
                    voiceJournalId: journal.id,
                    channel: 'voice',
                    subject: journal.title || 'Voice Journal',
                    snippet: journal.content?.substring(0, 200) || '',
                    content: journal.content || '',
                    participants: journal.contactEmail ? JSON.stringify([journal.contactEmail]) : '[]',
                    outcomes: journal.insights || '',
                    createdAt: new Date().toISOString(),
                    updatedAt: new Date().toISOString(),
                  }),
                this.MAX_RETRIES,
                this.INITIAL_RETRY_DELAY_MS,
                'Create journal timeline entry'
              );
              count++;
            }
          } catch (error) {
            // Silently skip individual errors
          }
        }
        
        // Delay between batches to avoid rate limiting
        if (i + this.BATCH_SIZE < journals.length) {
          await this.delay(this.BATCH_DELAY_MS);
        }
      }
    } catch (error) {
      console.error('Error syncing voice journals:', error);
    }

    return count;
  }

  /**
   * Force a full sync now
   */
  async forceSyncNow(): Promise<void> {
    console.log('🔄 Forcing timeline sync...');
    await this.syncTimeline();
  }
}

// Export singleton instance
export const timelineAggregationService = new TimelineAggregationService();
